"use client"

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { createPortal } from "react-dom"
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Variants,
} from "motion/react"
import { cn } from "../../lib/utils"
import { EASE_OUT } from "../../lib/ease"
import { useHoverCapable } from "../../hooks/use-hover-capable"
import { Kbd } from "./kbd"
import { useFloatingOverlay } from "./use-floating-overlay"

type Side = "top" | "right" | "bottom" | "left"

export interface TooltipProps {
  /**
   * Tooltip body. Plain text or rich JSX — a paragraph, a list, a `<code>`
   * span, whatever helps explain the trigger. Long strings wrap inside the
   * tooltip's max width; pass a wider one via `className` (e.g. `max-w-sm`)
   * when the default 20rem cap is too tight.
   */
  label: ReactNode
  /** Keyboard shortcut chip, for example "⌘U". */
  shortcut?: string
  /** Suppress the tooltip (for example, while the trigger's popover is open). */
  disabled?: boolean
  /**
   * Side of the trigger the tooltip prefers. Default "top". It flips to the
   * opposite side when the preferred one has no room.
   */
  side?: Side
  /** Delay before showing, in milliseconds. Default 450. */
  delay?: number
  children: ReactNode
  className?: string
  /**
   * Classes for the inline wrapper placed around the trigger. A trigger that
   * is a flex child needs its wrapper to carry the sizing, otherwise wrapping
   * it takes the constraint off and, for instance, stops a label truncating.
   */
  triggerClassName?: string
}

/** Gap between trigger and tooltip, in pixels. */
const GAP = 8

const TRANSFORM_ORIGIN: Record<Side, string> = {
  top: "center bottom",
  bottom: "center top",
  left: "right center",
  right: "left center",
}

/**
 * Initial offset in the direction away from the trigger, so the content
 * originates near the trigger and eases into its resting position.
 */
const OFFSET_FROM: Record<Side, { x?: number; y?: number }> = {
  top: { y: 8 },
  bottom: { y: -8 },
  left: { x: 8 },
  right: { x: -8 },
}

function buildVariants(side: Side): Variants {
  const offset = OFFSET_FROM[side]
  return {
    initial: {
      opacity: 0,
      scale: 0.9,
      filter: "blur(5px)",
      x: offset.x ?? 0,
      y: offset.y ?? 0,
    },
    animate: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      x: 0,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 380,
        damping: 30,
        mass: 0.7,
        opacity: { duration: 0.14, ease: EASE_OUT },
        filter: { duration: 0.18, ease: EASE_OUT },
      },
    },
    exit: {
      opacity: 0,
      scale: 0.94,
      filter: "blur(3px)",
      x: (offset.x ?? 0) * 0.6,
      y: (offset.y ?? 0) * 0.6,
      transition: { duration: 0.12, ease: EASE_OUT },
    },
  }
}

const REDUCED_VARIANTS: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.14, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.1, ease: EASE_OUT } },
}

/**
 * Once any tooltip has just closed, neighbouring tooltips open without the
 * initial delay, so moving along a toolbar feels instant after the first one.
 */
const WARM_WINDOW_MS = 300
let lastHiddenAt = 0

/**
 * Dark hover/focus tooltip with an optional keyboard-shortcut chip. Wraps its
 * child in an inline span and observes hover/focus on the whole subtree, so it
 * works around any trigger (button, popover) without cloning it. The content is
 * rendered into a body portal and animates in with a spring.
 *
 * Placement goes through {@link useFloatingOverlay}, so a trigger in any corner
 * flips the tooltip to the side that has room, slides it along the edge it
 * would run past, and caps its width to the space left rather than trailing
 * off-screen.
 */
export const Tooltip = ({
  label,
  shortcut,
  disabled,
  side = "top",
  delay = 450,
  children,
  className,
  triggerClassName,
}: TooltipProps) => {
  const [open, setOpen] = useState(false)
  const id = useId()
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const reduce = useReducedMotion()
  const canHover = useHoverCapable()
  const openRef = useRef(false)

  const { refs, overlayStyles, placement, isPositioned } = useFloatingOverlay({
    open,
    placement: side,
    gap: GAP,
  })

  /** The side the tooltip landed on, which the entry motion travels away from. */
  const resolvedSide = placement.split("-")[0] as Side

  const show = useCallback(
    (viaHover: boolean) => {
      if (disabled || (viaHover && !canHover)) return
      clearTimeout(timer.current)
      const warm = Date.now() - lastHiddenAt < WARM_WINDOW_MS
      timer.current = setTimeout(() => setOpen(true), warm ? 0 : delay)
    },
    [disabled, canHover, delay],
  )

  const hide = useCallback(() => {
    clearTimeout(timer.current)
    if (openRef.current) lastHiddenAt = Date.now()
    setOpen(false)
  }, [])

  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => () => clearTimeout(timer.current), [])

  useEffect(() => {
    if (disabled) {
      clearTimeout(timer.current)
      setOpen(false)
    }
  }, [disabled])

  const variants = useMemo(
    () => (reduce ? REDUCED_VARIANTS : buildVariants(resolvedSide)),
    [reduce, resolvedSide],
  )

  return (
    <span
      ref={refs.setReference}
      className={cn("relative inline-flex", triggerClassName)}
      aria-describedby={open ? id : undefined}
      onMouseEnter={() => show(true)}
      onMouseLeave={hide}
      onFocusCapture={() => show(false)}
      onBlurCapture={hide}
      onClickCapture={hide}
    >
      {children}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <span
                ref={refs.setFloating}
                aria-hidden
                /**
                 * `z-[110]` sits above `Modal`'s overlay (`z-[100]`, see
                 * `modal.tsx:285`), matching `menu.tsx`. Both portal to the
                 * body, so at `z-50` a tooltip raised from a control inside a
                 * modal painted behind the modal itself.
                 */
                className="pointer-events-none z-[110]"
                style={{
                  ...overlayStyles,
                  /**
                   * Held back until the first measurement lands, so the tooltip
                   * never paints at the viewport origin. The exit animation
                   * keeps the last rendered value, which is already visible.
                   */
                  visibility: isPositioned ? undefined : "hidden",
                }}
              >
                <motion.span
                  id={id}
                  role="tooltip"
                  variants={variants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  style={{ transformOrigin: TRANSFORM_ORIGIN[resolvedSide] }}
                  className={cn(
                    "flex max-w-xs items-center gap-1.5 rounded-v2-md bg-v2-foreground px-2 py-1 text-v2-xs font-medium text-v2-background shadow-v2-floating",
                    className,
                  )}
                >
                  <span className="min-w-0 whitespace-normal break-words">
                    {label}
                  </span>
                  {shortcut && <Kbd keys={shortcut} tone="inverted" />}
                </motion.span>
              </span>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </span>
  )
}
